import { ButtonHTMLAttributes, forwardRef, ReactElement } from 'react'
import { Slot, Slottable } from '@radix-ui/react-slot'
import { cva, type VariantProps } from 'class-variance-authority'

import { cn } from '@/lib/utils'

const buttonVariants = cva(
  [
    'relative inline-flex items-center justify-center gap-[2rem] px-[2.5rem] py-[1.5rem] h-[5rem] w-auto rounded-[.7rem] overflow-hidden  ',
    'before:absolute before:top-0 before:left-0 before:w-full before:h-full',
    'before:transition-colors before:duration-500 before:ease-out-back',
    '[&>*]:relative [&>*]:inline-flex [&>*]:overflow-hidden',
    '[&>*>span]:transition-transform [&>*>span]:duration-300 [&>*>span]:ease-out-cubic',
    '[&>*>span]:hover:-translate-y-full',
    '[&>*]:after:content-[attr(data-label)] [&>*]:after:translate-y-full [&>*]:after:w-full [&>*]:after:text-current [&>*]:after:h-full [&>*]:after:text-label [&>*]:after:font-condensed [&>*]:after:uppercase [&>*]:after:absolute [&>*]:after:top-0 [&>*]:after:left-0',
    '[&>*]:after:transition-transform [&>*]:after:duration-300 [&>*]:after:ease-out-cubic',
    '[&>*]:hover:after:translate-y-0 [&>*]:disabled:after:hidden'
  ],
  {
    variants: {
      variant: {
        light: 'text-black before:bg-primary hover:before:bg-primary-hover',
        dark: 'text-black before:bg-white hover:before:bg-white/80'
      }
    },
    defaultVariants: {
      variant: 'light'
    }
  }
)

export type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean
    icon?: ReactElement
  }

const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ children, variant, icon, className, asChild = false, ...props }, ref) => {
    const Component = asChild ? Slot : 'button'

    return (
      <Component
        ref={ref}
        className={cn(buttonVariants({ variant, className }))}
        {...props}
      >
        <Slottable>{children}</Slottable>
        {icon}
      </Component>
    )
  }
)

export default Button
